import { useState, useEffect, useMemo } from 'react';
import API from '@/lib/api';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  ChevronDown, ChevronUp, Upload, Image as ImageIcon, Send, Eye, Link2,
  Users, Building2, Home, X
} from 'lucide-react';
import { toast } from 'sonner';

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

function generateBlastHTML(data) {
  const {
    imageUrl, address, askingPrice, arv, folio, propertyType,
    beds, baths, unitCount, livingArea, lotSize, yearBuilt,
    subdivision, occupancy, description, photosLink,
    senderName, senderEmail, senderPhone, senderCompany
  } = data;

  const priceFormatted = askingPrice ? `$${Number(askingPrice).toLocaleString()}` : 'TBD';
  const arvFormatted = arv ? `$${Number(arv).toLocaleString()}` : '';
  const isSF = propertyType !== 'Multifamily';
  const bedsLine = isSF
    ? `${beds || '—'}/${baths || '—'}`
    : `${unitCount || '—'} Units`;

  return `
<div style="font-family:Arial,Helvetica,sans-serif;max-width:600px;margin:0 auto;background:#1a1a2e;color:#fff;border-radius:12px;overflow:hidden;">
  ${imageUrl ? `<img src="${imageUrl}" alt="Property" style="width:100%;height:auto;display:block;object-fit:cover;max-height:350px;" />` : ''}
  <div style="padding:24px 28px 28px;">
    <div style="text-align:center;margin-bottom:20px;">
      <span style="font-size:18px;font-weight:700;letter-spacing:1px;color:#ff6b35;">OFF MARKET EXCLUSIVE DEAL</span>
    </div>
    <div style="margin-bottom:16px;">
      <p style="font-size:16px;font-weight:700;color:#fff;margin:0 0 4px;">${address || 'Address TBD'}</p>
    </div>
    <table style="width:100%;border-collapse:collapse;margin-bottom:18px;">
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;width:40%;">Marketing Price</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:15px;font-weight:700;color:#ff6b35;">${priceFormatted}</td>
      </tr>
      ${arvFormatted ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">ARV</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;font-weight:600;color:#4ade80;">${arvFormatted}</td>
      </tr>` : ''}
      ${folio ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Folio</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;color:#fff;">${folio}</td>
      </tr>` : ''}
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">${isSF ? 'Bed/Bath' : 'Units'}</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;font-weight:600;color:#fff;">${bedsLine}</td>
      </tr>
      ${livingArea ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Living Area</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;color:#fff;">${Number(livingArea).toLocaleString()} Sq Ft</td>
      </tr>` : ''}
      ${lotSize ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Lot Size</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;color:#fff;">${Number(lotSize).toLocaleString()} Sq Ft</td>
      </tr>` : ''}
      ${yearBuilt ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Year Built</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;color:#fff;">${yearBuilt}</td>
      </tr>` : ''}
      ${subdivision ? `<tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Subdivision</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;color:#fff;">${subdivision}</td>
      </tr>` : ''}
      <tr>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);color:#aaa;font-size:13px;">Occupancy</td>
        <td style="padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.1);font-size:14px;font-weight:600;color:${occupancy === 'Vacant' ? '#4ade80' : '#fdab3d'};">${occupancy || 'TBD'}</td>
      </tr>
    </table>
    ${description ? `<div style="margin-bottom:18px;">
      <p style="font-size:12px;color:#aaa;text-transform:uppercase;letter-spacing:1px;margin:0 0 8px;">Condition &amp; Features</p>
      <p style="font-size:13px;color:#ddd;line-height:1.6;margin:0;">${description.replace(/\n/g, '<br>')}</p>
    </div>` : ''}
    ${photosLink ? `<div style="text-align:center;margin-top:20px;">
      <a href="${photosLink}" style="display:inline-block;background:#ff6b35;color:#fff;text-decoration:none;padding:12px 28px;border-radius:8px;font-size:14px;font-weight:600;letter-spacing:0.5px;">View Photos</a>
    </div>` : ''}
    ${(senderName || senderEmail || senderPhone || senderCompany) ? `
    <div style="border-top:1px solid rgba(255,255,255,0.1);margin-top:24px;padding-top:16px;">
      <p style="font-size:11px;color:#aaa;text-transform:uppercase;letter-spacing:1px;margin:0 0 8px;">Contact</p>
      ${senderName ? `<p style="font-size:14px;font-weight:600;color:#fff;margin:0 0 2px;">${senderName}</p>` : ''}
      ${senderCompany ? `<p style="font-size:12px;color:#aaa;margin:0 0 2px;">${senderCompany}</p>` : ''}
      ${senderEmail ? `<p style="font-size:12px;margin:0 0 2px;"><a href="mailto:${senderEmail}" style="color:#ff6b35;text-decoration:none;">${senderEmail}</a></p>` : ''}
      ${senderPhone ? `<p style="font-size:12px;color:#aaa;margin:0;"><a href="tel:${senderPhone}" style="color:#aaa;text-decoration:none;">${senderPhone}</a></p>` : ''}
    </div>` : ''}
  </div>
</div>`;
}

export default function DealBlastSection({ lead, recipients, recipientType, onSendComplete, preselectedIds }) {
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  // User profile for email signature
  const [senderProfile, setSenderProfile] = useState({ name: '', email: '', phone: '', company: '' });
  useEffect(() => {
    API.get('/user/profile').then(res => {
      setSenderProfile({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        company: res.data.company || '',
      });
    }).catch(() => {});
  }, []);

  // Blast form fields — auto-populate from lead data
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [livingArea, setLivingArea] = useState(lead?.sqft ? String(lead.sqft) : '');
  const [lotSize, setLotSize] = useState(lead?.lot_size ? String(lead.lot_size) : '');
  const [yearBuilt, setYearBuilt] = useState(lead?.year_built ? String(lead.year_built) : '');
  const [subdivision, setSubdivision] = useState(lead?.subdivision || '');
  const [unitCount, setUnitCount] = useState(lead?.unit_count ? String(lead.unit_count) : '');
  const [occupancy, setOccupancy] = useState(lead?.occupancy || 'Vacant');
  const [description, setDescription] = useState('');
  const [photosLink, setPhotosLink] = useState('');

  // Recipient selection
  const [selectionMode, setSelectionMode] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const isBuyers = recipientType === 'buyers';

  useEffect(() => {
    if (!preselectedIds?.length) return;
    setSelectionMode('individual');
    setSelectedIds(preselectedIds);
    setExpanded(true);
  }, [preselectedIds]);
  const label = isBuyers ? 'Buyers' : 'Wholesalers';
  const Icon = isBuyers ? Users : Building2;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreviewUrl(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => { setImageFile(null); setImagePreviewUrl(''); };

  const toggleId = (rid) => {
    setSelectedIds(prev => prev.includes(rid) ? prev.filter(x => x !== rid) : [...prev, rid]);
  };

  const blastData = useMemo(() => ({
    imageUrl: imagePreviewUrl,
    address: lead?.property_address || '',
    askingPrice: lead?.marketing_price || lead?.asking_price || 0,
    arv: lead?.arv || 0,
    folio: lead?.folio || '',
    propertyType: lead?.property_type || 'Single Family',
    beds: lead?.bedrooms || '',
    baths: lead?.bathrooms || '',
    unitCount,
    livingArea,
    lotSize,
    yearBuilt,
    subdivision,
    occupancy,
    description,
    photosLink,
    senderName: senderProfile.name,
    senderEmail: senderProfile.email,
    senderPhone: senderProfile.phone,
    senderCompany: senderProfile.company,
  }), [lead, imagePreviewUrl, livingArea, lotSize, yearBuilt, subdivision, unitCount, occupancy, description, photosLink, senderProfile]);

  const previewHtml = useMemo(() => generateBlastHTML(blastData), [blastData]);

  const getSelectedRecipients = () => {
    if (!isBuyers) return recipients.filter(r => selectedIds.includes(r.id));
    if (selectionMode === 'category' && selectedCategory) {
      return recipients.filter(r => r.category === selectedCategory);
    }
    return recipients.filter(r => selectedIds.includes(r.id));
  };

  const handleSend = async () => {
    const selected = getSelectedRecipients();
    if (selected.length === 0) { toast.error(`Select at least one ${recipientType === 'buyers' ? 'buyer' : 'wholesaler'}`); return; }

    // Upload image first if present
    let uploadedImageUrl = '';
    if (imageFile) {
      try {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('lead_id', lead.id);
        const uploadRes = await API.post('/uploads/deal-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedImageUrl = uploadRes.data.url;
      } catch {
        toast.error('Failed to upload image. Sending without image.');
      }
    }

    // Generate final HTML with uploaded image URL
    const finalData = { ...blastData, imageUrl: uploadedImageUrl || '' };
    const finalHtml = generateBlastHTML(finalData);

    setSending(true);
    try {
      const payload = {
        lead_id: lead.id,
        html_content: finalHtml,
        subject: `Off Market Deal - ${lead.property_address || 'New Property'}`,
        recipient_emails: selected.map(r => r.email),
        recipient_type: recipientType,
      };
      const res = await API.post('/emails/send-blast', payload);
      toast.success(res.data.message || `Sent to ${selected.length} ${label.toLowerCase()}`);
      setExpanded(false);
      setShowPreview(false);
      if (onSendComplete) onSendComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const sfCount = recipients.filter(r => r.category === 'Single Family').length;
  const mfCount = recipients.filter(r => r.category === 'Multifamily').length;
  const isMF = (lead?.property_type || '').toLowerCase().includes('multi');

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid={`deal-blast-${recipientType}`}>
      <CardContent className="p-0">
        {/* Header - always visible */}
        <button
          className="w-full flex items-center justify-between p-5 text-left"
          onClick={() => setExpanded(!expanded)}
          data-testid={`toggle-blast-${recipientType}`}
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#0073ea]/10 flex items-center justify-center">
              <Icon className="h-4.5 w-4.5 text-[#0073ea]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Send to {label}</p>
              <p className="text-xs text-gray-500">
                {recipients.length} {label.toLowerCase()} available
                {isBuyers && ` (${sfCount} SF, ${mfCount} MF)`}
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {/* Expanded form */}
        {expanded && (
          <div className="px-5 pb-5 pt-0 space-y-4 border-t border-gray-100">
            {/* Image Upload */}
            <div className="space-y-1.5 pt-3">
              <Label className="text-gray-500 text-xs uppercase tracking-wider">Property Image</Label>
              {imagePreviewUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  <img src={imagePreviewUrl} alt="Property" className="w-full h-48 object-cover" />
                  <Button
                    variant="ghost" size="sm"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white h-7 w-7 p-0 rounded-full"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0073ea]/50 transition-colors">
                  <ImageIcon className="h-8 w-8 text-gray-300 mb-1" />
                  <span className="text-xs text-gray-500">Click to upload property image</span>
                  <input
                    type="file" accept="image/*" className="hidden"
                    data-testid={`blast-image-upload-${recipientType}`}
                    onChange={handleImageChange}
                  />
                </label>
              )}
            </div>

            {/* Extra Fields */}
            <div className="grid grid-cols-2 gap-3">
              {isMF ? (
                <div className="space-y-1.5">
                  <Label className="text-gray-500 text-xs">Unit Count</Label>
                  <Input value={unitCount} onChange={e => setUnitCount(e.target.value)} placeholder="4" className={inputClass} data-testid="blast-unit-count" />
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Living Area (Sq Ft)</Label>
                <Input value={livingArea} onChange={e => setLivingArea(e.target.value)} placeholder="1,780" className={inputClass} data-testid="blast-living-area" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Lot Size (Sq Ft)</Label>
                <Input value={lotSize} onChange={e => setLotSize(e.target.value)} placeholder="7,350" className={inputClass} data-testid="blast-lot-size" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Year Built</Label>
                <Input value={yearBuilt} onChange={e => setYearBuilt(e.target.value)} placeholder="1957" className={inputClass} data-testid="blast-year-built" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Subdivision</Label>
                <Input value={subdivision} onChange={e => setSubdivision(e.target.value)} placeholder="Riverland Village" className={inputClass} data-testid="blast-subdivision" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-500 text-xs">Occupancy</Label>
                <Select value={occupancy} onValueChange={setOccupancy}>
                  <SelectTrigger className={inputClass} data-testid="blast-occupancy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Vacant" className="text-gray-900">Vacant</SelectItem>
                    <SelectItem value="Occupied" className="text-gray-900">Occupied</SelectItem>
                    <SelectItem value="TBD" className="text-gray-900">TBD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider">Description / Condition & Features</Label>
              <Textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Pool home, stainless steel appliances, shutters on all windows, carport + storage, no HOA, prime location near beaches..."
                className={`${inputClass} resize-none`}
                data-testid="blast-description"
              />
            </div>

            {/* Photos Link */}
            <div className="space-y-1.5">
              <Label className="text-gray-500 text-xs uppercase tracking-wider">Photos Link (Dropbox, Google Photos, etc.)</Label>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={photosLink}
                  onChange={e => setPhotosLink(e.target.value)}
                  placeholder="https://dropbox.com/sh/..."
                  className={`${inputClass} pl-9`}
                  data-testid="blast-photos-link"
                />
              </div>
            </div>

            <Separator className="bg-gray-200" />

            {/* Preview */}
            <div>
              <Button
                variant="outline" size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 mb-3"
                data-testid={`toggle-preview-${recipientType}`}
              >
                <Eye className="h-3.5 w-3.5 mr-1.5" /> {showPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
              {showPreview && (
                <div
                  data-testid={`blast-preview-${recipientType}`}
                  className="light-zone rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-white text-gray-900"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
                />
              )}
            </div>

            <Separator className="bg-gray-200" />

            {/* Recipient Selection */}
            <div className="space-y-3">
              <Label className="text-gray-500 text-xs uppercase tracking-wider">Select {label}</Label>
              {isBuyers && (
                <div className="flex gap-2 mb-2">
                  <button
                    data-testid="blast-mode-category"
                    onClick={() => setSelectionMode('category')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      selectionMode === 'category' ? 'bg-[#cce5ff] text-[#0073ea] border border-[#0073ea]/30' : 'text-gray-500 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >By Category</button>
                  <button
                    data-testid="blast-mode-individual"
                    onClick={() => setSelectionMode('individual')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      selectionMode === 'individual' ? 'bg-[#cce5ff] text-[#0073ea] border border-[#0073ea]/30' : 'text-gray-500 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >Individual</button>
                </div>
              )}

              {isBuyers && selectionMode === 'category' ? (
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className={inputClass} data-testid="blast-category-select">
                    <SelectValue placeholder="Select buyer category..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="Single Family" className="text-gray-900">
                      <span className="flex items-center gap-2"><Home className="h-3 w-3" /> Single Family ({sfCount})</span>
                    </SelectItem>
                    <SelectItem value="Multifamily" className="text-gray-900">
                      <span className="flex items-center gap-2"><Building2 className="h-3 w-3" /> Multifamily ({mfCount})</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-2">
                  {recipients.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No {label.toLowerCase()} available</p>
                  ) : [...recipients].sort((a, b) => (preselectedIds?.includes(b.id) ? 1 : 0) - (preselectedIds?.includes(a.id) ? 1 : 0)).map(r => (
                    <label
                      key={r.id}
                      data-testid={`blast-recipient-${r.id}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                        selectedIds.includes(r.id) ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedIds.includes(r.id)}
                        onCheckedChange={() => toggleId(r.id)}
                        className="border-gray-300"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{r.name}</p>
                        <p className="text-xs text-gray-500 truncate">{r.company ? `${r.company} - ` : ''}{r.email}</p>
                      </div>
                      {r.category && (
                        <Badge variant="outline" className={`text-[10px] ${
                          r.category === 'Multifamily' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}>{r.category === 'Multifamily' ? 'MF' : 'SF'}</Badge>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Send Button */}
            <Button
              data-testid={`send-blast-${recipientType}`}
              onClick={handleSend}
              disabled={sending}
              className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white"
            >
              {sending ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send to {isBuyers
                    ? (selectionMode === 'category' && selectedCategory
                      ? `${recipients.filter(r => r.category === selectedCategory).length} ${selectedCategory} Buyers`
                      : `${selectedIds.length} Buyer${selectedIds.length !== 1 ? 's' : ''}`)
                    : `${selectedIds.length} Wholesaler${selectedIds.length !== 1 ? 's' : ''}`
                  }
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
